import { Controller } from "@hotwired/stimulus"
import { patch } from "@rails/request.js"

// Manages the "highlight photo" selection within the photos drop zone.
// When a photo tile is starred, its signed_id is written to the hidden
// item[highlight_photo_signed_id] input so the server can persist it.
export default class extends Controller {
  static targets = ["input", "previews"]
  static values = { householdId: String, itemId: String }

  connect() {
    this._refreshHighlights()

    if (this.hasPreviewsTarget) {
      // Watches direct tile children being added or removed
      this._childObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          mutation.removedNodes.forEach(node => {
            if (node.nodeType !== Node.ELEMENT_NODE) return
            const removedId = node.querySelector?.("input[type='hidden']")?.value
            if (removedId && removedId === this.inputTarget.value) {
              this.inputTarget.value = ""
              this._saveHighlight("")
            }
          })
        }
        this._refreshHighlights()
      })
      this._childObserver.observe(this.previewsTarget, { childList: true })

      // Watches data-state transitions on tiles (uploading → done)
      this._attrObserver = new MutationObserver((mutations) => {
        for (const mutation of mutations) {
          if (mutation.target.dataset.state !== "done") continue
          if (this.inputTarget.value) continue
          const signedId = this._getSignedId(mutation.target)
          if (signedId) {
            this.inputTarget.value = signedId
            this._saveHighlight(signedId)
            this._updateSidebarPreview(signedId)
            this._refreshHighlights()
          }
        }
      })
      this._attrObserver.observe(this.previewsTarget, {
        subtree: true,
        attributeFilter: ["data-state"]
      })
    }
  }

  disconnect() {
    this._childObserver?.disconnect()
    this._attrObserver?.disconnect()
  }

  select(event) {
    event.stopPropagation()
    const tile = event.currentTarget.closest("[data-state]")
    if (!tile) return

    const signedId = this._getSignedId(tile)
    if (!signedId) return

    // Toggle: clicking the already-selected tile removes the highlight
    this.inputTarget.value = this.inputTarget.value === signedId ? "" : signedId
    this._refreshHighlights()
    this._updateSidebarPreview(this.inputTarget.value)
    this._saveHighlight(this.inputTarget.value)
  }

  _saveHighlight(signedId) {
    if (!this.hasItemIdValue) return
    const body = new FormData()
    body.append("item[highlight_photo_signed_id]", signedId)
    patch(`/households/${this.householdIdValue}/items/${this.itemIdValue}/highlight`, { body })
  }

  _updateSidebarPreview(signedId) {
    if (!this.hasItemIdValue) return
    const frame = document.getElementById(`item-${this.itemIdValue}-highlight-preview`)
    if (!frame) return
    frame.src = `/households/${this.householdIdValue}/items/${this.itemIdValue}/highlight?signed_id=${encodeURIComponent(signedId)}`
  }

  _refreshHighlights() {
    const current = this.inputTarget.value
    const tiles = this.element.querySelectorAll("[data-state='done'], [data-state='existing']")

    tiles.forEach(tile => {
      if (!tile.querySelector("[data-highlight-btn]")) {
        tile.classList.add("group")
        tile.appendChild(this._createHighlightBtn())
      }

      const signedId = this._getSignedId(tile)
      const isHighlight = !!(signedId && signedId === current)
      this._updateTileState(tile, isHighlight)
    })
  }

  _getSignedId(tile) {
    return tile.dataset.signedId || tile.querySelector("input[type='hidden']")?.value || null
  }

  _createHighlightBtn() {
    const btn = document.createElement("button")
    btn.type = "button"
    btn.dataset.highlightBtn = true
    btn.dataset.action = "click->photo-highlight#select"
    btn.setAttribute("aria-label", "Set as cover photo")
    btn.setAttribute("title", "Set as cover photo")
    btn.className = [
      "absolute bottom-1.5 right-1.5 rounded-full p-1.5 transition-all duration-150",
      "bg-hearth-900/80 text-hearth-400",
      "opacity-40 group-hover:opacity-100 active:opacity-100",
      "hover:text-warm-amber-400 hover:scale-110 active:scale-110"
    ].join(" ")
    btn.innerHTML = this._starOutlineSvg()
    return btn
  }

  _updateTileState(tile, isHighlight) {
    if (isHighlight) {
      tile.style.outline = "2px solid var(--color-warm-amber-400)"
      tile.style.outlineOffset = "-2px"
    } else {
      tile.style.outline = ""
      tile.style.outlineOffset = ""
    }

    const btn = tile.querySelector("[data-highlight-btn]")
    if (btn) {
      if (isHighlight) {
        btn.className = [
          "absolute bottom-1.5 right-1.5 rounded-full p-1 transition-all duration-150",
          "bg-warm-amber-500/25 text-warm-amber-400",
          "opacity-100",
          "hover:scale-110"
        ].join(" ")
        btn.innerHTML = this._starFilledSvg()
      } else {
        btn.className = [
          "absolute bottom-1.5 right-1.5 rounded-full p-1.5 transition-all duration-150",
          "bg-hearth-900/80 text-hearth-400",
          "opacity-40 group-hover:opacity-100 active:opacity-100",
          "hover:text-warm-amber-400 hover:scale-110 active:scale-110"
        ].join(" ")
        btn.innerHTML = this._starOutlineSvg()
      }
    }

    // Cover badge
    let badge = tile.querySelector("[data-cover-badge]")
    if (isHighlight && !badge) {
      badge = document.createElement("div")
      badge.dataset.coverBadge = true
      badge.className = [
        "absolute top-1.5 left-1.5",
        "bg-warm-amber-500 text-hearth-950",
        "text-[10px] font-bold leading-none px-1.5 py-1 rounded-md",
        "pointer-events-none"
      ].join(" ")
      badge.textContent = "Cover"
      tile.appendChild(badge)
    } else if (!isHighlight && badge) {
      badge.remove()
    }
  }

  _starOutlineSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
      <path stroke-linecap="round" stroke-linejoin="round" d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"/>
    </svg>`
  }

  _starFilledSvg() {
    return `<svg xmlns="http://www.w3.org/2000/svg" class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
      <path d="M11.48 3.499a.562.562 0 0 1 1.04 0l2.125 5.111a.563.563 0 0 0 .475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 0 0-.182.557l1.285 5.385a.562.562 0 0 1-.84.61l-4.725-2.885a.562.562 0 0 0-.586 0L6.982 20.54a.562.562 0 0 1-.84-.61l1.285-5.386a.562.562 0 0 0-.182-.557l-4.204-3.602a.562.562 0 0 1 .321-.988l5.518-.442a.563.563 0 0 0 .475-.345L11.48 3.5Z"/>
    </svg>`
  }
}
